import React from 'react';
import clsx from 'clsx';
import { Field, useFormState } from 'react-final-form';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';

import WithLabel, { LABEL_STYLES } from '../../../../../../components/with-label/WithLabel';
import WithError from '../../../../../../components/with-error/WithError';
import DocsButton from '../../../../../../scout-new/components/generation-section/components/docs-button/DocsButton';
import { Input } from '../../../../../../components/common-inputs';
import Icon from '../../../../../../icons/Icon';
import { ICONS } from '../../../../../../icons';
import { BUTTON_STYLES } from '../../../../../../components/common-button/CommonButton';
import TooltipButton from '../../../../../../components/tooltip-button/TooltipButton';
import { ACTIV8_MESSAGE_GENERATE_TABS, ASSISTANT_TYPES } from '../../../../../../constants';
import { useProject } from '../../../../../../context/ProjectContext';

import scss from './GenerationForm.module.scss';

function GenerationFormInner({ onSubmit, onFileUpload }) {
  const { t } = useTranslation('scout');
  const { actions } = useProject();
  const { errors } = useFormState();

  const isGenerating = useSelector((state) => state.activ8Messages.isGenerating);
  const isFormLoading = useSelector((state) => state.activ8Messages.isFormLoading);
  const isResetting = useSelector((state) => state.activ8Messages.isResetting);
  const isFileUploading = useSelector((state) => state.scout.isFileUploading);
  const generateTab = useSelector((state) => state.activ8Messages.generateTab);
  const threadId = useSelector((state) => state.activ8Messages.threadId);

  const disableForm =
    isFormLoading || isResetting || isGenerating || generateTab !== ACTIV8_MESSAGE_GENERATE_TABS.GENERATED;

  const handleResetSession = () => {
    actions('clearSession');
  };

  return (
    <form className={scss.form} id='activ8generation_form' onSubmit={onSubmit}>
      <div className={scss.fieldsSection}>
        <div className={scss.fieldsRow}>
          <Field name='url'>
            {({ meta, input }) => (
              <WithLabel variant={LABEL_STYLES.ROW} label='URL' isBold isRequired isTwoDots>
                <WithError isShowError={meta.touched && meta.error} errorText={t(meta.error)} isAbsolute>
                  <Input
                    size='sm'
                    name={input.name}
                    value={input.value}
                    onChange={input.onChange}
                    onFocus={input.onFocus}
                    onBlur={input.onBlur}
                    disabled={disableForm}
                    isError={meta.touched && meta.error}
                    onEnter={onSubmit}
                  />
                </WithError>
              </WithLabel>
            )}
          </Field>

          <DocsButton
            selectedTypeOption={{ type: ASSISTANT_TYPES.FILE }}
            onFileUpload={onFileUpload}
            disabled={disableForm || isFileUploading}
            isLoading={isFileUploading}
          />

          <Field name='focusOn'>
            {({ meta, input }) => (
              <WithLabel variant={LABEL_STYLES.ROW} label={t('topicIdeas')} isBold isTwoDots>
                <WithError isShowError={meta.touched && meta.error} errorText={t(meta.error)} isAbsolute>
                  <Input
                    size='sm'
                    name={input.name}
                    value={input.value}
                    onChange={input.onChange}
                    onFocus={input.onFocus}
                    onBlur={input.onBlur}
                    disabled={disableForm}
                    onEnter={onSubmit}
                  />
                </WithError>
              </WithLabel>
            )}
          </Field>

          <Field name='byPass'>
            {({ meta, input }) => (
              <WithLabel variant={LABEL_STYLES.ROW} label={t('bypass')} isBold isTwoDots>
                <WithError isShowError={meta.touched && meta.error} errorText={t(meta.error)} isAbsolute>
                  <Input
                    size='sm'
                    name={input.name}
                    value={input.value}
                    onChange={input.onChange}
                    onFocus={input.onFocus}
                    onBlur={input.onBlur}
                    disabled={disableForm}
                    onEnter={onSubmit}
                  />
                </WithError>
              </WithLabel>
            )}
          </Field>
        </div>
      </div>

      <div className={scss.buttonsSection}>
        <TooltipButton
          styleColor={BUTTON_STYLES.PRIMARY}
          type='submit'
          onClick={onSubmit}
          isLoading={isGenerating}
          disabled={disableForm}>
          {t('generate')}
        </TooltipButton>

        <TooltipButton
          styleColor={BUTTON_STYLES.SECONDARY}
          onClick={handleResetSession}
          disabled={disableForm || !threadId}
          tooltip={isGenerating && t('messagesGenerating')}>
          {t('resetSession')}
          <Icon icon={ICONS.Reset} className={clsx(isResetting && scss.spinAnimation)} />
        </TooltipButton>
      </div>
    </form>
  );
}

export default GenerationFormInner;
